import { Box, FormControl, Grid, Card, CardContent, Typography, Chip, Avatar, CircularProgress, Switch, Tooltip } from "@mui/material";
import MaterialTable from "material-table";
import SelectableSearch from "../../../Base/views/SelectableSearch";
import { useState, useEffect } from "react";
import CommonDatePicker from "../../../Base/views/CommonDatePicker";
import CustomToggle from "../../../Base/views/CustomToggle";
import CustomPagination from "../../../Base/views/CustomPagination";
import { GetDailyProductivityAction, GetReportRecruiterAction } from "../../../../../store/ATS/Report/Action";
import { useDispatch, useSelector } from "react-redux";
import { ConvertDateTime, DateConvertInDbFormat, getEmployeeId } from "../../../Base/helper/baseFunctions";
import Chart from "react-apexcharts";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import VideoCameraFrontOutlinedIcon from "@mui/icons-material/VideoCameraFrontOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
// ─── Brand ────────────────────────────────────────────────────────────────────
const BRAND = "#8B44FF";
const medalStyles = [
    { bg: "#FFF0B3", color: "#B8860B" },
    { bg: "#E2E8F0", color: "#475569" },
    { bg: "#F5D0B5", color: "#9A3412" },
];



// ─── KPI Card ─────────────────────────────────────────────────────────────────
const KpiCard = ({ icon, value, label, color }) => (
    <Card sx={{ height: "100%" }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar sx={{ bgcolor: `${color}18`, color, width: 36, height: 36 }}>{icon}</Avatar>
                <Box>
                    <Typography fontWeight={800} fontSize="1.35rem" color={"text.primary"} lineHeight={1}>{value}</Typography>
                    <Typography fontSize="0.75rem" color="text.secondary">{label}</Typography>
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const ProductivityNewReport = () => {
    const dispatch = useDispatch();
    const [localIsDashboardView, setLocalIsDashboardView] = useState(true);
    const date = new Date();
    const fyYear = date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
    const default_from = `${fyYear}-04-01`;
    const default_to = DateConvertInDbFormat();

    const [userData, setUserData] = useState({ load: true, values: [] });
    const [selectedRows, setSelectedRows] = useState([]);
    const [allRecruiter, setAllRecruiter] = useState(true);
    const [atsRecruiterData, setAtsRecruiterData] = useState([]);
    const [recruiterReady, setRecruiterReady] = useState(false);
    const [filters, setFilters] = useState({ primary_recruiter_filter: "", from_date: default_from, to_date: default_to });

    const { atsDailyProductivity, isProdLoading } = useSelector(state => state.AtsReducer?.AtsReportReducer);

    // ── Load recruiter list based on My Reportees toggle ─────────────────────
    useEffect(() => {
        const login_employee_id = allRecruiter ? null : getEmployeeId();
        dispatch(GetReportRecruiterAction({ login_employee_id }, (resp) => {
            const recruiterList = resp?.data?.data || [];
            setUserData({ load: false, values: recruiterList });
            setAtsRecruiterData(recruiterList);
            if (allRecruiter) {
                setFilters((prev) => ({ ...prev, primary_recruiter_filter: "" }));
            } else {
                setFilters((prev) => ({ ...prev, primary_recruiter_filter: recruiterList }));
            }
            setRecruiterReady(true);
        }));
    }, [allRecruiter, dispatch]);

    // ── Fetch productivity data on filter change ──────────
    useEffect(() => {
        if (!recruiterReady) return;
        dispatch(GetDailyProductivityAction({
            from_date: filters?.from_date,
            to_date: filters?.to_date,
            primary_recruiter: filters?.primary_recruiter_filter ? `{${filters?.primary_recruiter_filter?.map((item) => item.ats_users_id).join(",")}}` : null,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, recruiterReady]);

    const handleFilter = (event, value) => {
        if (["primary_recruiter_filter"].includes(event)) {
            setFilters({ ...filters, [event]: value || "" });
        }
        if (["from_date", "to_date"].includes(event?.target?.name)) {
            setFilters({ ...filters, [event?.target?.name]: event?.target?.value });
        }
    };

    // ── Export Selected Rows as CSV ───────────────────────────────────────────
    const handleExportSelected = () => {
        const headers = [
            { label: "Date", key: "modified_date" },
            { label: "Mandate ID", key: "mandate_id" },
            { label: "Job Title", key: "job_title" },
            { label: "Recruiter", key: "recruiter_name" },
            { label: "Candidates Proposed", key: "shortlisted" },
            { label: "Profiles Shared to Client", key: "profile_shared_to_client" },
            { label: "Interviews Scheduled", key: "interviews_scheduled" },
            { label: "Business Unit", key: "businessunit_name" },
            { label: "Designation", key: "designation" },
            { label: "Client Name", key: "client_name" },
            { label: "Team Leader", key: "team_leader" },
            { label: "Job Creation Date", key: "job_creation_date" },
        ];
        const csvData = [headers.map(header => header.label)];
        selectedRows?.forEach(row => {
            const rowData = headers?.map(header => {
                if (header.key === "modified_date" || header.key === "job_creation_date") {
                    return row[header.key] ? ConvertDateTime(row[header.key]) : "";
                }
                return row[header.key] !== undefined && row[header.key] !== null ? row[header.key] : "";
            });
            csvData.push(rowData);
        });
        const csv = csvData.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const downloadLink = document.createElement("a");
        downloadLink.href = url;
        downloadLink.download = "Productivity.csv";
        downloadLink.setAttribute("target", "_blank");
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(url);
    };

    // ── Derived KPIs & Footer Totals ──────────────────────────────────────────
    const activities = atsDailyProductivity || [];
    const totalProfiles = activities?.reduce((s, r) => s + (parseInt(r?.shortlisted) || 0), 0) || 0;
    const totalSharedProfiles = activities?.reduce((s, r) => s + (parseInt(r?.profile_shared_to_client) || 0), 0) || 0;
    const totalInterviews = activities?.reduce((s, r) => s + (parseInt(r?.interviews_scheduled) || 0), 0) || 0;
    const activeRecruiters = new Set(activities?.map(r => r?.recruiter_id)?.filter(Boolean)).size;

    const hasNonZeroSum = totalProfiles || totalSharedProfiles || totalInterviews;
    const displayableKeys = hasNonZeroSum ? [
        ["Candidates Proposed", totalProfiles],
        ["Profiles Shared to Client", totalSharedProfiles],
        ["Interviews Scheduled", totalInterviews],
    ] : [];

    // ── Aggregate by recruiter for charts ─────────────────────────────────────
    const recruiterMap = {};
    activities?.forEach(r => {
        const id = r?.recruiter_id || "Unknown";
        const name = r?.recruiter_name || "Unknown";
        if (!recruiterMap[id]) recruiterMap[id] = { id, name, profiles: 0, shared: 0, interviews: 0 };
        recruiterMap[id].profiles += parseInt(r?.shortlisted) || 0;
        recruiterMap[id].shared += parseInt(r?.profile_shared_to_client) || 0;
        recruiterMap[id].interviews += parseInt(r?.interviews_scheduled) || 0;
    });
    const recruiterList = Object.values(recruiterMap).slice(0, 12);

    const stackedBar = {
        options: {
            chart: { type: "bar", fontFamily: "Nunito Sans", toolbar: { show: false }, stacked: true },
            colors: [BRAND, "#2CA02C", "#4E79A7"],
            plotOptions: { bar: { columnWidth: "55%", borderRadius: 4 } },
            dataLabels: { enabled: false },
            grid: { borderColor: "#F1F5F9", strokeDashArray: 4 },
            xaxis: {
                categories: recruiterList?.map(r => r.name.split(" ")[0]),
                labels: { style: { fontSize: "11px", fontWeight: 600 }, rotate: -30 },
            },
            yaxis: { labels: { style: { fontSize: "11px", colors: "#94A3B8" } } },
            legend: { position: "top", fontSize: "12px", markers: { radius: 3 } },
        },
        series: [
            { name: "Candidates Proposed", data: recruiterList?.map(r => r.profiles) },
            { name: "Profiles Shared to Client", data: recruiterList?.map(r => r.shared) },
            { name: "Interviews Scheduled", data: recruiterList?.map(r => r.interviews) },
        ],
    };

    const top6 = Object.values(recruiterMap)
        ?.sort((a, b) => b.profiles - a.profiles)
        .slice(0, 6);

    const donutChart = {
        options: {
            chart: { type: "donut", fontFamily: "Nunito Sans" },
            labels: top6.map(r => r?.name.split(" ")[0]),
            colors: ["#8B44FF", "#4E79A7", "#10B981", "#F28E2B", "#E15759", "#76B7B2"],
            dataLabels: { formatter: (val, opts) => opts.w.globals.series[opts.seriesIndex] },
            legend: { position: "bottom", fontFamily: "Nunito Sans", fontWeight: 600 },
            plotOptions: {
                pie: { donut: { size: "65%", labels: { show: true, total: { show: true, label: "Proposed", fontSize: "12px" } } } },
            },
        },
        series: top6.map(r => r.profiles),
    };

    const defaultCellStyle = {
        padding: "5px 5px 5px 10px",
        fontSize: "0.78rem",
    };

    // ── Table columns ─────────────────────────────────────────────────────────
    const columns = [
        {
            title: "Date", field: "modified_date",
            render: r => r?.modified_date ? ConvertDateTime(r?.modified_date) : "—",
            cellStyle: { ...defaultCellStyle, whiteSpace: "nowrap" },
        },
        {
            title: "Mandate ID", field: "mandate_id",
            render: r => r?.mandate_id || "—",
            cellStyle: { ...defaultCellStyle, whiteSpace: "nowrap" },
        },
        {
            title: "Job Title", field: "job_title",
            render: r => r?.job_title || "N/A",
            cellStyle: { ...defaultCellStyle, whiteSpace: "nowrap" },
        },
        {
            title: "Recruiter", field: "recruiter_name",
            render: r => r?.recruiter_name || "—",
            cellStyle: defaultCellStyle,
        },
        {
            title: "Candidates Proposed", field: "shortlisted", type: "numeric",
            cellStyle: { ...defaultCellStyle, textAlign: "center" }, headerStyle: { textAlign: "center" },
            render: r => (
                <Chip label={parseInt(r?.shortlisted) || 0} size="small"
                    sx={{
                        bgcolor: (parseInt(r?.shortlisted) || 0) > 0 ? "#F3EDFD" : "#F1F5F9",
                        color: (parseInt(r?.shortlisted) || 0) > 0 ? BRAND : "#94A3B8",
                        fontWeight: 700,
                        fontSize: "0.78rem",
                        height: "22px",
                    }}
                />
            ),
        },
        {
            title: "Profiles Shared to Client", field: "profile_shared_to_client", type: "numeric",
            cellStyle: { ...defaultCellStyle, textAlign: "center" }, headerStyle: { textAlign: "center" },
            render: r => (
                <Chip label={parseInt(r?.profile_shared_to_client) || 0} size="small"
                    sx={{
                        bgcolor: (parseInt(r?.profile_shared_to_client) || 0) > 0 ? "#ECFDF5" : "#F1F5F9",
                        color: (parseInt(r?.profile_shared_to_client) || 0) > 0 ? "#2CA02C" : "#94A3B8",
                        fontWeight: 700,
                        fontSize: "0.78rem",
                        height: "22px",
                    }}
                />
            ),
        },
        {
            title: "Interviews Scheduled", field: "interviews_scheduled", type: "numeric",
            cellStyle: { ...defaultCellStyle, textAlign: "center" }, headerStyle: { textAlign: "center" },
            render: r => (
                <Chip label={parseInt(r?.interviews_scheduled) || 0} size="small"
                    sx={{
                        bgcolor: (parseInt(r?.interviews_scheduled) || 0) > 0 ? "#EFF6FF" : "#F1F5F9",
                        color: (parseInt(r?.interviews_scheduled) || 0) > 0 ? "#4E79A7" : "#94A3B8",
                        fontWeight: 700,
                        fontSize: "0.78rem",
                        height: "22px",
                    }}
                />
            ),
        },
        {
            title: "Business Unit", field: "businessunit_name",
            render: r => r?.businessunit_name || "—",
            cellStyle: defaultCellStyle,
        },
        {
            title: "Designation", field: "designation",
            render: r => r?.designation?.trim?.() || r?.designation || "N/A",
            cellStyle: defaultCellStyle,
        },
        {
            title: "Client Name", field: "client_name",
            render: r => r?.client_name || "—",
            cellStyle: defaultCellStyle,
        },
        {
            title: "Team Leader", field: "team_leader",
            render: r => r?.team_leader?.trim?.() || "—",
            cellStyle: defaultCellStyle,
        },
        {
            title: "Job Creation Date", field: "job_creation_date",
            render: r => r?.job_creation_date ? ConvertDateTime(r?.job_creation_date) : "—",
            cellStyle: { ...defaultCellStyle, whiteSpace: "nowrap" },
        },
    ];

    return (
        <>
            <Box sx={{ mt: 1 }}>
                {/* ── Filters & View Toggle ─────────────────────────────── */}
                <Grid container columnSpacing={1} rowSpacing={1} mb={2} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        <SelectableSearch
                            isOptionEqualToValue={(o, v) => +o?.ats_users_id === +v?.ats_users_id}
                            limitTags={1}
                            label={allRecruiter ? "All Recruiter" : "My Recruiter"}
                            multiple={true}
                            value={filters?.primary_recruiter_filter}
                            loading={() => setUserData({ load: false, values: atsRecruiterData })}
                            fieldLabel="first_name"
                            required={false}
                            grouping={true}
                            variant="outlined"
                            name="primary_recruiter_filter"
                            onChangeEvent={handleFilter}
                            data={userData}
                            size="small"
                            styles={{
                                "& .MuiOutlinedInput-root": {
                                    minHeight: 40,
                                    height: "auto",
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={2.5} md={2}>
                        <FormControl fullWidth>
                            <CommonDatePicker
                                size="small"
                                name="from_date"
                                label="From Date"
                                pickerType="date"
                                onKeyDown={e => e.preventDefault()}
                                pickerFormat="dd/MM/yyyy"
                                DefaultDTvalue={filters.from_date}
                                maxDate={filters.to_date ? new Date(filters.to_date) : new Date()}
                                handleChange={value =>
                                    handleFilter({ target: { name: "from_date", value: value ? `${value.getFullYear()}-${("0" + (value.getMonth() + 1)).slice(-2)}-${("0" + value.getDate()).slice(-2)}` : "" } })
                                }
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={2.5} md={2}>
                        <FormControl fullWidth>
                            <CommonDatePicker
                                size="small"
                                name="to_date"
                                label="To Date"
                                pickerType="date"
                                onKeyDown={e => e.preventDefault()}
                                pickerFormat="dd/MM/yyyy"
                                DefaultDTvalue={filters.to_date}
                                maxDate={new Date()}
                                minDate={filters.from_date ? new Date(filters.from_date) : null}
                                handleChange={value =>
                                    handleFilter({ target: { name: "to_date", value: value ? `${value.getFullYear()}-${("0" + (value.getMonth() + 1)).slice(-2)}-${("0" + value.getDate()).slice(-2)}` : "" } })
                                }
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs="auto">
                        <Tooltip
                            title={
                                allRecruiter
                                    ? "Currently showing all recruiters. Click to view only your reportees."
                                    : "Currently showing your reportees. Click to view all recruiters."
                            }
                            arrow
                            placement="top"
                        >
                            <Box
                                onClick={() => setAllRecruiter(!allRecruiter)}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5, px: 0.75, py: 0.8,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    whiteSpace: "nowrap",
                                    cursor: "pointer",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        color: "text.secondary",
                                    }}
                                >
                                    My Reportees
                                </Typography>

                                <Switch
                                    checked={!allRecruiter}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        setAllRecruiter(!e.target.checked);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    size="small"
                                    inputProps={{ "aria-label": "My Reportees" }}
                                    sx={{
                                        width: 34,
                                        height: 22,
                                        p: 0,
                                        "& .MuiSwitch-switchBase": {
                                            p: "3px",
                                            "&.Mui-checked": {
                                                transform: "translateX(12px)",
                                            },
                                        },
                                        "& .MuiSwitch-thumb": {
                                            width: 16,
                                            height: 16,
                                        },
                                        "& .MuiSwitch-track": {
                                            borderRadius: 11,
                                        },
                                    }}
                                />
                            </Box>
                        </Tooltip>
                    </Grid>
                    {isProdLoading && (
                        <Grid item xs="auto" sx={{ display: "flex", alignItems: "center" }}>
                            <CircularProgress size={22} disableShrink/>
                        </Grid>
                    )}
                    <Grid item xs sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" }, alignItems: "center" }}>
                        {setLocalIsDashboardView && (
                            <Tooltip title={localIsDashboardView ? "Switch to Table View" : "Switch to Dashboard View"}>
                              <span>
                                <CustomToggle
                                    isChecked={!localIsDashboardView}
                                    handleToggleChange={() => setLocalIsDashboardView(!localIsDashboardView)}
                                    Yestext={"Table"}
                                    NoText={"Dashboard"}
                                    style={{ height: "37px", labelPadding: "2px 8px" }}
                                />
                              </span>
                            </Tooltip>
                        )}
                    </Grid>
                </Grid>

                {localIsDashboardView ? (
                    <>
                        {/* ── KPI Cards ─────────────────────────────────────────── */}
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid item xs={6} sm={6} md={3}>
                                <KpiCard icon={<ShareOutlinedIcon sx={{ fontSize: 18 }} />} value={totalProfiles} label="Candidates Proposed" color={BRAND} />
                            </Grid>
                            <Grid item xs={6} sm={6} md={3}>
                                <KpiCard icon={<SendOutlinedIcon sx={{ fontSize: 18 }} />} value={totalSharedProfiles} label="Profiles Shared to Client" color="#2CA02C" />
                            </Grid>
                            <Grid item xs={6} sm={6} md={3}>
                                <KpiCard icon={<VideoCameraFrontOutlinedIcon sx={{ fontSize: 18 }} />} value={totalInterviews} label="Interviews Scheduled" color="#4E79A7" />
                            </Grid>
                            <Grid item xs={6} sm={6} md={3}>
                                <KpiCard icon={<GroupsOutlinedIcon sx={{ fontSize: 18 }} />} value={activeRecruiters} label="Active Recruiters" color="#10B981" />
                            </Grid>
                        </Grid>

                        {/* ── Charts ────────────────────────────────────────────── */}
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={7}>
                                <Card sx={{ height: "100%" }}>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Activity Breakdown</Typography>
                                        <Typography variant="caption" color="text.secondary">Candidates proposed · Profiles shared · Interviews scheduled per recruiter</Typography>
                                        {recruiterList.length > 0 ? (
                                            <Chart options={stackedBar.options} series={stackedBar.series} type="bar" height={260} />
                                        ) : (
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "#94A3B8" }}>
                                                <Typography fontSize="0.82rem">No data for the selected period</Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <Card sx={{ height: "100%" }}>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Profiles Proposed Distribution</Typography>
                                        <Typography variant="caption" color="text.secondary">Top 6 recruiters by candidates proposed</Typography>
                                        {top6?.length > 0 && top6?.some(r => r?.profiles > 0) ? (
                                            <Chart options={donutChart.options} series={donutChart.series} type="donut" height={260} />
                                        ) : (
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "#94A3B8" }}>
                                                <Typography fontSize="0.82rem">No data for the selected period</Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* ── Top Performers Leaderboard ─────────────────────────── */}
                        <Grid container spacing={1.5} pb={1}>
                            <Grid item xs={12}>
                                <Card>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                                             <EmojiEventsIcon sx={{ fontSize: 16, mr: 0.5, color: "#FECB03", verticalAlign: "text-bottom" }} />
                                            Top Performers
                                        </Typography>
                                        <Grid container spacing={1.5}>
                                            {top6?.slice(0, 5).map((recruiter, i) => (
                                                <Grid item xs={12} sm={6} md={2.4} key={recruiter.id || i}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, p: 1.2, bgcolor: "#ffffff", borderRadius: 1.5, border: "1px solid #F1F5F9" }}>
                                                        <Avatar sx={{ width: 32, height: 32, bgcolor: i < 3 ? medalStyles[i].bg : "#E2E8F0", color: i < 3 ? medalStyles[i].color : "#475569", fontSize: "0.75rem", fontWeight: 800 }} > #{i + 1} </Avatar>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography variant="body2" fontWeight={600} fontSize="0.82rem" color={"text.primary"} noWrap>{recruiter?.name}</Typography>
                                                            <Tooltip title={`${recruiter?.profiles} proposed · ${recruiter?.shared} shared · ${recruiter?.interviews} interviews`} placement='right-start'>
                                                                <Typography variant="caption" color="text.secondary" noWrap display="block">
                                                                    {recruiter?.profiles} proposed · {recruiter?.shared} shared · {recruiter?.interviews} interviews
                                                                </Typography>
                                                            </Tooltip>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                        {activities?.length === 0 && (
                                            <Typography fontSize="0.8rem" color="text.secondary">No data for the selected period</Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </>
                ) : (
                    /* ── Table Grid View ────────────────────────────────────────── */
                    <Box sx={{ mt: 1 }}>
                        <MaterialTable
                            columns={columns}
                            data={activities}
                            options={{
                                maxBodyHeight: "calc(100vh - 310px)",
                                minBodyHeight: "calc(100vh - 310px)",
                                sorting: true,
                                search: true,
                                searchFieldAlignment: "right",
                                searchAutoFocus: true,
                                searchFieldVariant: "standard",
                                filtering: false,
                                exportButton: false,
                                exportAllData: true,
                                paging: true,
                                pageSizeOptions: [50, 100],
                                pageSize: 50,
                                showTextRowsSelected: true,
                                showSelectAllCheckbox: true,
                                selection: true,
                                actionsColumnIndex: -1,
                                draggable: false,
                                headerStyle: {
                                    fontSize: "0.78rem",
                                    whiteSpace: "nowrap",
                                    padding: "5px 5px 5px 7px",
                                },
                                cellStyle: defaultCellStyle,
                            }}
                            actions={[
                                {
                                    icon: () => <CloudDownloadIcon />,
                                    tooltip: 'Export Selected Rows',
                                    onClick: () => handleExportSelected(),
                                    hidden: selectedRows.length === 0,
                                },
                            ]}
                            onSelectionChange={(rows) => setSelectedRows(rows)}
                            components={{
                                Pagination: props => <CustomPagination {...props} displayableKeys={displayableKeys} />,
                            }}
                            title="Productivity Activity Log"
                        />
                    </Box>
                )}
            </Box>
        </>
    );
};

export default ProductivityNewReport;
